import React from 'react';
import UserSearch from './UserSearch';
import Pagination from './Pagination';
import { ResponsiveList, ResponsiveContainer, ResponsiveCard } from '../common/ResponsiveLayout';
import MobileCard, { MobileCardContent } from '../common/MobileCard';
import { useMobile } from '../../hooks/useMobile';

const UserList = ({
  users,
  currentUserId,
  userRole,
  onEdit,
  onDelete,
  onView,
  onActivateDeactivate,
  loading = false,
  // Search and filter props
  searchTerm,
  onSearchChange,
  roleFilter,
  onRoleFilterChange,
  sortBy,
  onSortChange,
  sortOrder,
  onSortOrderChange,
  onClearFilters,
  // Pagination props
  currentPage,
  totalPages,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange
}) => {
  const formatName = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.full_name || 'N/A';
  };

  const formatRole = (role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'Super Admin';
      case 'COACH':
        return 'Coach';
      case 'CLIENT':
        return 'Client';
      default:
        return role;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Only SUPER_ADMIN or the user themself can successfully save an edit
  // (mirrors canEdit() in EditProfileModal.jsx) — hide the affordance otherwise
  const canEditUser = (user) => {
    return userRole === 'SUPER_ADMIN' || user.id === currentUserId;
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-4">
        <h2 className="text-xl font-bold mb-4">User List</h2>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-2 text-gray-600">Loading users...</span>
        </div>
      </div>
    );
  }

  // Render user as mobile card
  const renderUserCard = (user, index) => (
    <MobileCard
      key={user.id}
      onPress={() => onView && onView(user)}
      actions={getUserActions(user)}
      showBorder={true}
      animated={true}
      className={!user.is_active ? 'bg-gray-100 opacity-75' : ''}
    >
      <MobileCardContent
        title={
          <div className="flex items-center gap-2">
            <span className={!user.is_active ? 'text-gray-500' : ''}>{formatName(user)}</span>
            {!user.is_active && (
              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                Inactive
              </span>
            )}
          </div>
        }
        subtitle={user.email || 'No email'}
        badge={<RoleBadge role={user.role} isActive={user.is_active} />}
        metadata={[
          {
            icon: '📅',
            label: formatDate(user.created_at)
          },
          ...(user.coach_name ? [{
            icon: '👨‍🏫',
            label: `Coach: ${user.coach_name}`
          }] : [])
        ]}
        actions={getUserActions(user)}
      />
    </MobileCard>
  );

  // Render user as table row
  const renderUserRow = (user, index) => (
    <tr key={user.id} className={`hover:bg-gray-50 ${!user.is_active ? 'bg-gray-100' : ''}`}>
      <td className="px-4 py-4">
        <div className="flex flex-col">
          <div className={`text-sm font-medium ${!user.is_active ? 'text-gray-500' : 'text-gray-900'}`}>
            {formatName(user)}
            {!user.is_active && (
              <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                Inactive
              </span>
            )}
          </div>
          <div className={`text-xs ${!user.is_active ? 'text-gray-400' : 'text-gray-500'}`}>
            {user.email || 'No email'}
          </div>
          <div className={`text-xs ${!user.is_active ? 'text-gray-300' : 'text-gray-400'}`}>
            {formatDate(user.created_at)}
          </div>
        </div>
      </td>
      <td className="px-4 py-4">
        <RoleBadge role={user.role} isActive={user.is_active} />
      </td>
      <td className="px-4 py-4">
        <div className={`text-sm ${!user.is_active ? 'text-gray-500' : 'text-gray-900'}`}>
          {user.coach_name || 'None'}
        </div>
      </td>
      <td className="px-4 py-4 text-right space-x-2">
        <button
          onClick={() => onView && onView(user)}
          className="text-blue-600 hover:text-blue-900 text-sm font-medium"
          title="View user profile"
        >
          View
        </button>
        {canEditUser(user) && (
          <button
            onClick={() => onEdit(user)}
            className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
            title="Edit user"
          >
            Edit
          </button>
        )}
        {onActivateDeactivate && user.id !== currentUserId && (
          <button
            onClick={() => onActivateDeactivate(user)}
            className={`text-sm font-medium ${
              user.is_active
                ? 'text-orange-600 hover:text-orange-900'
                : 'text-green-600 hover:text-green-900'
            }`}
            title={user.is_active ? 'Deactivate user' : 'Activate user'}
          >
            {user.is_active ? 'Deactivate' : 'Activate'}
          </button>
        )}
        <button
          onClick={() => onDelete(user.id)}
          className="text-red-600 hover:text-red-900 text-sm font-medium"
          title="Delete user"
        >
          Delete
        </button>
      </td>
    </tr>
  );

  // Get user actions for mobile cards
  const getUserActions = (user) => {
    const actions = [];

    // View action - first in the menu
    actions.push({
      label: 'View',
      icon: '👁️',
      onClick: () => onView && onView(user),
      variant: 'secondary'
    });

    // Edit action - only when this user can actually save the change
    if (canEditUser(user)) {
      actions.push({
        label: 'Edit',
        icon: '✏️',
        onClick: () => onEdit(user),
        variant: 'secondary'
      });
    }

    // Add activate/deactivate action if handler is provided and it's not the current user
    if (onActivateDeactivate && user.id !== currentUserId) {
      actions.push({
        label: user.is_active ? 'Deactivate' : 'Activate',
        icon: user.is_active ? '🚫' : '✅',
        onClick: () => onActivateDeactivate(user),
        variant: user.is_active ? 'warning' : 'success'
      });
    }

    // Delete action - last in the menu
    actions.push({
      label: 'Delete',
      icon: '🗑️',
      onClick: () => onDelete(user.id),
      variant: 'danger'
    });

    return actions;
  };

  return (
    <ResponsiveContainer className="space-y-4">
      {/* Search and Filter Controls */}
      <UserSearch
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        roleFilter={roleFilter}
        onRoleFilterChange={onRoleFilterChange}
        sortBy={sortBy}
        onSortChange={onSortChange}
        sortOrder={sortOrder}
        onSortOrderChange={onSortOrderChange}
        onClearFilters={onClearFilters}
      />

      {/* User List */}
      <ResponsiveCard>
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            User List {totalCount !== undefined && `(${totalCount} total)`}
          </h2>
        </div>

        <div className="p-4">
          <ResponsiveList
            items={users}
            loading={loading}
            renderCard={renderUserCard}
            renderTableRow={renderUserRow}
            tableHeaders={['User', 'Role', 'Coach', 'Actions']}
            empty={
              <div className="text-center py-8 text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="mt-2">No users found</p>
                {searchTerm && (
                  <p className="text-sm">Try adjusting your search criteria</p>
                )}
              </div>
            }
          />
        </div>

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="border-t border-gray-200">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalCount={totalCount}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          </div>
        )}
      </ResponsiveCard>
    </ResponsiveContainer>
  );
};

// Role badge component
const RoleBadge = ({ role, isActive = true }) => {
  const formatRole = (role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'Super Admin';
      case 'COACH':
        return 'Coach';
      case 'CLIENT':
        return 'Client';
      default:
        return role;
    }
  };

  const getColorClasses = () => {
    if (!isActive) {
      return 'bg-gray-100 text-gray-600';
    }
    
    switch (role) {
      case 'SUPER_ADMIN':
        return 'bg-purple-100 text-purple-800';
      case 'COACH':
        return 'bg-blue-100 text-blue-800';
      case 'CLIENT':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getColorClasses()}`}>
      {formatRole(role)}
    </span>
  );
};

export default UserList;